import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

const ROUNDED = {
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
} as const;

/** Delayed 150ms fade-in (120ms hold) so fast loads never flash a skeleton. */
export const SKELETON_REVEAL_CLASS =
  'animate-[skeleton-reveal_150ms_ease-out_120ms_both]';

interface SkeletonProps {
  /** Size it to the final element (h-10 title, h-5 body line, h-4 caption…). */
  className?: string;
  rounded?: keyof typeof ROUNDED;
}

/** Shimmering placeholder block. Always mirror the final element's box 1:1. */
export function Skeleton({ className, rounded = 'md' }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative block overflow-hidden bg-seeker-800 before:absolute before:inset-0 before:animate-[skeleton-shimmer_1.4s_ease-in-out_infinite] before:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.05),transparent)] before:content-['']",
        ROUNDED[rounded],
        className,
      )}
    />
  );
}

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/** Body-text placeholder: 14px bars on 20px lines, last line 60% wide. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <span aria-hidden="true" className={cn('flex flex-col', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className="flex h-5 items-center">
          <Skeleton
            className={cn('h-3.5 w-full', index === lines - 1 && 'w-3/5')}
          />
        </span>
      ))}
    </span>
  );
}

interface SkeletonGroupProps {
  /** Announced to assistive tech, e.g. "Loading releases". */
  label?: string;
  children: ReactNode;
  className?: string;
}

/** Wraps a skeleton layout as one `role="status"` region with the delayed reveal. */
export function SkeletonGroup({
  label = 'Loading',
  children,
  className,
}: SkeletonGroupProps) {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn(SKELETON_REVEAL_CLASS, className)}
    >
      {children}
    </div>
  );
}
